import { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Collapse, Container, Divider, Grid, IconButton, Link, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { List, ListItem } from 'components/ui'
import { useMappedTranslation, usePageTitle } from 'hooks'
import { Paper } from 'oneui'
import { validateUrl } from 'utils/common'
import i18n, { getGroupIndexByLocale, supportedLanguageList } from 'utils/i18n'

import IconArrow from './assets/images/arrow.svg'

const Language = () => {
  const t = useMappedTranslation()
  usePageTitle(t('language.title'))
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = window.location.hostname
  const cnDomainRegx = /.cn$/
  const cnName = 'China'

  const [openTab, setOpenTab] = useState(getGroupIndexByLocale(i18n.language))

  const [searchParams] = useSearchParams()
  let backUrl = searchParams.get('back') || ''
  backUrl = validateUrl(`${window.location.protocol}//${window.location.host}${backUrl}`)

  const handleListClick = (index) => {
    if (openTab === index) {
      setOpenTab(-1)
    } else {
      setOpenTab(index)
    }
  }

  const handleLanguageChange = (language: { locale: string; language: string }) => {
    i18n.changeLanguage(language.locale)

    /**
     * This project has multiple sub SPA projects.
     * Router control is not available between SPA projects.
     * However Language control is common feature. So it should use exact link instead of react-router.
     * document.referrer is not available for react-router.
     */
    if (backUrl === '' || backUrl === null) {
      window.location.href = '/'
    } else {
      const url = new URL(backUrl)
      navigate(`${url.pathname}${url.search}${url.hash}`, { ...location })
    }
  }

  return (
    <Container>
      <Paper
        id='SectionPaper'
        sx={{ padding: { xs: '40px 18px 24px 24px', md: '60px 96px 48px' }, borderRadius: '26px', border: '0' }}
      >
        <Stack justifyContent='flex-start' sx={{ height: '100%' }}>
          <Box sx={{ marginBottom: '48px', height: { xs: 'inherit', md: '44px' } }}>
            <Typography variant='h2' sx={{ fontWeight: 300 }}>
              {t('language.title')}
            </Typography>
          </Box>

          <Box sx={{ width: '100%', height: '100%' }}>
            <List sx={{ p: '0' }}>
              {supportedLanguageList.map((group, index) => (
                <Box key={group.group} sx={{ width: '100%' }}>
                  <ListItem
                    title={group.group}
                    fullWidth={true}
                    sx={{ padding: '18px 0' }}
                    stackSx={{ flex: '1' }}
                    onClick={() => handleListClick(index)}
                    secondaryAction={
                      <IconButton
                        tabIndex={-1}
                        sx={{ padding: '0' }}
                        onClick={() => {
                          handleListClick(index)
                        }}
                        aria-label={
                          openTab === index
                            ? t('iam.Accessibility.Button.Attribute.view.less')
                            : t('iam.Accessibility.Button.Attribute.view.more')
                        }
                        data-testid='IconButton5D2D2EE9'
                      >
                        <Box
                          component='img'
                          src={IconArrow}
                          sx={{ transform: openTab === index ? 'rotate(-90deg)' : 'rotate(90deg)' }}
                          alt=''
                        />
                      </IconButton>
                    }
                    data-testid='ListItemFDE68E43'
                  />
                  {/* <ListItem
                    sx={{ padding: '24px 0' }}
                    secondaryAction={
                      <IconButton
                        onClick={() => {
                          if (openTab === index) {
                            setOpenTab(-1)
                          } else {
                            setOpenTab(index)
                          }
                        }}
                      >
                        <Box
                          component='img'
                          src={IconArrow}
                          sx={{ transform: openTab === index ? 'rotate(-90deg)' : 'rotate(90deg)' }}
                        />
                      </IconButton>
                    }
                  >
                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                      {group.group}
                    </Typography>
                  </ListItem> */}
                  <Collapse in={index === openTab}>
                    <Grid container sx={{ padding: '8px 0 0' }} columnSpacing={'24px'}>
                      {group.countryList.map((country) =>
                        !cnDomainRegx.test(currentPath) && country.countryName === cnName ? (
                          ''
                        ) : (
                          <Grid key={country.countryCode} item xs={12} md={6} lg={4} sx={{ mb: '20px' }}>
                            {country.languages.map((language) => (
                              <Link
                                key={language.locale}
                                component='button'
                                type='button'
                                underline='none'
                                sx={{
                                  cursor: 'pointer',
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left'
                                }}
                                onClick={() => handleLanguageChange(language)}
                                data-testid='Box4A298E4C'
                              >
                                <Typography
                                  variant='label1'
                                  color='text.primary'
                                  sx={{ textTransform: 'none', whiteSpace: 'pre-wrap' }}
                                >
                                  {country.countryName + ' / '}
                                </Typography>
                                <Typography
                                  variant='label1'
                                  sx={{
                                    color: theme.palette.primary.main,
                                    textTransform: 'none'
                                  }}
                                >
                                  {language.language}
                                </Typography>
                              </Link>
                            ))}
                          </Grid>
                        )
                      )}
                    </Grid>
                  </Collapse>
                  {supportedLanguageList.length === index + 1 ? '' : <Divider />}
                </Box>
              ))}
            </List>
          </Box>
        </Stack>
      </Paper>
    </Container>
  )
}

export default Language
